import paths from "config/routePaths";
import Authorization from "policies/Authorization";
import React from "react";
import { useParams } from "react-router";
import { Route, Routes } from "react-router-dom";
import authorizePage from "./authorizePage";
import AdminDashboardContainer from "./pages/admin/AdminDashboardContainer";
import AvailabilityOverrides from "./pages/availability/AvailabilityOverrides";
import DefaultAvailability from "./pages/availability/DefaultAvailability";
import CompanyCreateContainer from "./pages/company/CompanyCreateContainer";
import CompanyEditContainer from "./pages/company/CompanyEditContainer";
import CompanyIndexContainer from "./pages/company/CompanyIndexContainer";
import CustomerSettings from "./pages/customerSettings/CustomerSettings";
import DowntimeReasons from "./pages/downtimeReasons/DowntimeReasons";
import Home from "./pages/home/Home";
import LaborSummary from "./pages/labor/LaborSummary";
import Lines from "./pages/line/Lines";
import NotFound from "./pages/notFound/NotFound";
import PlannedBreaks from "./pages/plannedBreaks/PlannedBreaks";
import Shifts from "./pages/shift/Shifts";
import SiteCreateContainer from "./pages/site/SiteCreateContainer";
import SiteEditContainer from "./pages/site/SiteEditContainer";
import SiteIndexContainer from "./pages/site/SiteIndexContainer";
import TeardownTimes from "./pages/teardownTimes/TeardownTimes";
import UserCreateContainer from "./pages/user/UserCreateContainer";
import UserEditContainer from "./pages/user/UserEditContainer";
import UserIndexContainer from "./pages/user/UserIndexContainer";
import UserPreferences from "./pages/user/UserPreferences";
import Version from "./pages/version/Version";

function CompanyEdit({ policy }) {
  const { id } = useParams();

  return authorizePage(policy.canUpdate(Authorization.EntityTypes.Company), <CompanyEditContainer id={id} />);
}

function SiteCreate({ policy }) {
  const { companyId } = useParams();

  return authorizePage(policy.canUpdate(Authorization.EntityTypes.Site), <SiteCreateContainer companyId={companyId} />);
}

function SiteEdit({ policy }) {
  const { id } = useParams();

  return authorizePage(policy.canUpdate(Authorization.EntityTypes.Site), <SiteEditContainer id={id} />);
}

function UserCreateCompany({ policy }) {
  const { companyId } = useParams();

  return authorizePage(policy.canUpdate(Authorization.EntityTypes.User), <UserCreateContainer companyId={companyId} />);
}

function UserCreateSite({ policy }) {
  const { companyId, siteId } = useParams();

  return authorizePage(
    policy.canUpdate(Authorization.EntityTypes.User),
    <UserCreateContainer companyId={companyId} siteId={siteId} />,
  );
}

function UserEdit({ policy }) {
  const { id } = useParams();

  return authorizePage(policy.canUpdate(Authorization.EntityTypes.User), <UserEditContainer id={id} />);
}

function RouterComponent() {
  const policy = Authorization.usePolicy();

  return (
    <div>
      <Routes>
        <Route element={<Home />} exact path={paths.home} />
        <Route
          element={authorizePage(policy.canShow(Authorization.EntityTypes.UsageData), <AdminDashboardContainer />)}
          path={paths.adminDashboard}
        />
        <Route
          element={authorizePage(policy.canUpdate(Authorization.EntityTypes.Company), <CompanyIndexContainer />)}
          exact
          path={paths.companyIndex}
        />
        <Route
          element={authorizePage(policy.canUpdate(Authorization.EntityTypes.Company), <CompanyCreateContainer />)}
          exact
          path={paths.companyNew}
        />
        <Route element={<CompanyEdit policy={policy} />} path={paths.companyEdit(":id")} />
        <Route element={<LaborSummary />} exact path={paths.laborSummary} />
        <Route
          element={authorizePage(policy.canUpdate(Authorization.EntityTypes.Site), <SiteIndexContainer />)}
          exact
          path={paths.siteIndex}
        />
        <Route
          element={authorizePage(policy.canUpdate(Authorization.EntityTypes.Site), <SiteCreateContainer />)}
          exact
          path={paths.siteNew}
        />
        <Route element={<SiteCreate policy={policy} />} exact path={paths.siteNewForCompany(":companyId")} />
        <Route element={<SiteEdit policy={policy} />} path={paths.siteEdit(":id")} />
        <Route
          element={authorizePage(policy.canUpdate(Authorization.EntityTypes.TeardownTime), <TeardownTimes />)}
          exact
          path={paths.teardownTimes}
        />
        <Route
          element={authorizePage(policy.canUpdate(Authorization.EntityTypes.User), <UserIndexContainer />)}
          exact
          path={paths.userIndex}
        />
        <Route
          element={authorizePage(policy.canUpdate(Authorization.EntityTypes.User), <UserCreateContainer />)}
          exact
          path={paths.userNew}
        />
        <Route element={<UserCreateCompany policy={policy} />} exact path={paths.userNewForCompany(":companyId")} />
        <Route
          element={<UserCreateSite policy={policy} />}
          exact
          path={paths.userNewForSite(":companyId", ":siteId")}
        />
        <Route element={<UserEdit policy={policy} />} path={paths.userEdit(":id")} />
        <Route
          element={authorizePage(policy.canUpdate(Authorization.EntityTypes.CustomerSettings), <CustomerSettings />)}
          path={paths.customerSettings}
        />
        <Route
          element={authorizePage(
            policy.canUpdate(Authorization.EntityTypes.DefaultAvailability),
            <DefaultAvailability />,
          )}
          path={paths.defaultAvailability}
        />
        <Route
          element={authorizePage(
            policy.canUpdate(Authorization.EntityTypes.AvailabilityOverride),
            <AvailabilityOverrides />,
          )}
          path={paths.availabilityOverrides}
        />
        <Route
          element={authorizePage(policy.canUpdate(Authorization.EntityTypes.DowntimeReason), <DowntimeReasons />)}
          path={paths.downtimeReasons}
        />

        <Route
          element={authorizePage(policy.canUpdate(Authorization.EntityTypes.Line), <Lines />)}
          path={paths.lines}
        />

        <Route
          element={authorizePage(policy.canUpdate(Authorization.EntityTypes.Shift), <Shifts />)}
          path={paths.shifts}
        />

        <Route
          element={authorizePage(policy.canUpdate(Authorization.EntityTypes.PlannedBreak), <PlannedBreaks />)}
          path={paths.plannedBreaks}
        />

        <Route
          element={authorizePage(policy.canUpdate(Authorization.EntityTypes.UserPreferences), <UserPreferences />)}
          path={paths.preferences}
        />

        <Route element={authorizePage(policy.canSeeVersion(), <Version />)} path={paths.version} />
        <Route element={<NotFound />} path="*" />
      </Routes>
    </div>
  );
}

export default RouterComponent;
